/**
 * accordion-animate.js
 * Adds smooth open/close transitions to every <details> element on the page.
 *
 * Works by intercepting clicks on <summary>, measuring the content height, and
 * animating via the Web Animations API before toggling the open attribute.
 */

(function () {
  const DURATION = 380; // ms
  const EASING   = 'cubic-bezier(0.16, 1, 0.3, 1)';

  /**
   * Returns the first non-summary child element of a <details> node,
   * which is the collapsible body.
   */
  function getBody(details) {
    return Array.from(details.children).find(function(el) {
      return el.tagName.toLowerCase() !== 'summary';
    }) || null;
  }

  /**
   * Animate a single <details> element open or closed.
   */
  function animate(details, opening) {
    var body = getBody(details);
    if (!body) return;

    // Cancel any in-progress animation on this element
    if (details._accordionAnim) {
      details._accordionAnim.cancel();
    }

    var startHeight = opening ? 0 : body.scrollHeight;
    var endHeight   = opening ? body.scrollHeight : 0;

    // Temporarily force open so we can read scrollHeight when opening
    if (opening) {
      details.setAttribute('open', '');
      // Re-measure after open attribute is set
      endHeight = body.scrollHeight;
    }

    body.style.overflow  = 'hidden';
    body.style.height    = startHeight + 'px';
    body.style.boxSizing = 'border-box';

    var anim = body.animate(
      [
        { height: startHeight + 'px', opacity: opening ? 0 : 1 },
        { height: endHeight   + 'px', opacity: opening ? 1 : 0.5 }
      ],
      { duration: DURATION, easing: EASING, fill: 'forwards' }
    );

    details._accordionAnim = anim;

    anim.onfinish = function () {
      details._accordionAnim = null;
      body.style.height   = '';
      body.style.overflow = '';
      body.style.opacity  = '';
      if (!opening) {
        details.removeAttribute('open');
      }
    };

    anim.oncancel = function () {
      body.style.height   = '';
      body.style.overflow = '';
    };
  }

  /**
   * Attach a click listener to a <details> element.
   * Animates all accordions including mobile menu submenus (+ / -).
   * Skips root full-screen slide containers (menu-drawer container, search modal).
   */
  function attachTo(details) {
    if (details._accordionBound) return;

    // Skip drawer/modal details elements – they use their own CSS animation logic
    if (details.closest('menu-drawer, header-drawer, details-modal, details-disclosure')) return;
    // Skip localization dropdowns
    if (details.closest('.localization-form, .share-button__fallback')) return;

    details._accordionBound = true;

    var summary = details.querySelector(':scope > summary');
    if (!summary) return;

    summary.addEventListener('click', function (e) {
      e.preventDefault();
      var isOpen = details.hasAttribute('open');
      animate(details, !isOpen);
    });
  }

  function init() {
    document.querySelectorAll('details').forEach(attachTo);

    // Watch for new <details> elements added to the DOM
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          // Early exit: skip nodes that contain no details elements
          var isDetails = node.tagName && node.tagName.toLowerCase() === 'details';
          var hasDetails = !isDetails && node.querySelector && node.querySelector('details');
          if (!isDetails && !hasDetails) return;
          if (isDetails) {
            attachTo(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('details').forEach(attachTo);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
